import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { AccessState, AppBootstrap, AppLocation, AppUser } from '@/lib/access';

interface Location extends AppLocation {
  name: string;
}

interface StoreState {
  user: AppUser | null;
  access: AccessState | null;
  token: string | null;
  isAuthenticated: boolean;
  sidebarCollapsed: boolean;
  selectedLocationId: number | null;
  locations: Location[];
  setUser: (user: AppUser | null) => void;
  setBootstrap: (bootstrap: AppBootstrap | null) => void;
  setToken: (token: string | null) => void;
  toggleSidebar: () => void;
  setSelectedLocationId: (id: number | null) => void;
  setLocations: (locations: Location[]) => void;
  logout: () => void;
}

export const useStore = create<StoreState>()(
  persist(
    (set) => ({
      user: null,
      access: null,
      token: null,
      isAuthenticated: false,
      sidebarCollapsed: false,
      selectedLocationId: null,
      locations: [],
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      // Не подставлять primary_location_id в шапку: по умолчанию «Все точки», пока пользователь сам не выберет точку.
      setBootstrap: (bootstrap) =>
        set({
          user: bootstrap?.user ?? null,
          access: bootstrap?.access ?? null,
          isAuthenticated: !!bootstrap?.user,
          locations: bootstrap?.available_locations ?? [],
        }),
      // Cookie-only: токены больше не хранятся в localStorage.
      // setToken оставлен как no-op ради обратной совместимости — его вызовы из старого кода
      // не должны ломать сборку. Очищаем legacy-ключи на всякий случай.
      setToken: (token) => {
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem('token');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
          } catch {
            /* private mode */
          }
        }
        set({ token: null });
      },
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSelectedLocationId: (id) => set({ selectedLocationId: id }),
      setLocations: (locations) => set({ locations }),
      logout: () => {
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem('token');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            localStorage.removeItem('web_admin_bootstrap');
          } catch {
            /* private mode */
          }
        }
        set({ user: null, access: null, token: null, isAuthenticated: false, locations: [], selectedLocationId: null });
      },
    }),
    {
      name: 'app-storage',
      version: 3,
      migrate: (persisted, version) => {
        if (!persisted || typeof persisted !== 'object') return persisted as StoreState;
        const next = { ...(persisted as Record<string, unknown>) };
        if (version < 2) next.selectedLocationId = null;
        if (version < 3) {
          // v3: больше не персистим access-token — обнуляем поле, кука сделает аутентификацию.
          next.token = null;
        }
        return next as StoreState;
      },
    }
  )
);

export const useAuthStore = useStore;
